import { useState } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { Search, ShoppingBag, Menu, X, Phone } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { LOGO_URL, BUSINESS } from '@/data/site';
import SearchOverlay from '@/components/SearchOverlay';
import AnnouncementBar from '@/components/AnnouncementBar';

const NAV = [
  { to: '/shop', label: 'Shop' },
  { to: '/deals', label: 'Deals' },
  { to: '/blog', label: 'Blog' },
  { to: '/service-area', label: 'Service Area' },
  { to: '/how-to-order', label: 'How to Order' },
  { to: '/about', label: 'About' },
  { to: '/contact', label: 'Contact' },
];

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Clicking SHOP while already inside a store category/product: a SPA
  // navigation can't reliably reset Ecwid's own view, so full-load /shop —
  // the store boots on its root grid every time.
  const shopReset = (e) => {
    if (window.location.pathname.startsWith('/shop') && window.location.hash.startsWith('#!')) {
      e.preventDefault();
      window.location.assign('/shop');
    }
  };

  return (
    <>
      <header className="fixed top-0 z-50 w-full border-b border-ink/10 bg-bone/80 backdrop-blur-xl">
        <AnnouncementBar />
        {/* slim utility strip — desktop only: hours, address, phone */}
        <div className="hidden border-b border-ink/5 lg:block">
          <div className="mx-auto flex h-8 max-w-7xl items-center justify-between px-4 text-[10px] font-semibold uppercase tracking-[0.18em] text-ash sm:px-6">
            <span data-testid="header-utility-hours">{BUSINESS.hours} · {BUSINESS.address}, {BUSINESS.city}, {BUSINESS.state}</span>
            <a href={BUSINESS.phoneHref} data-testid="header-phone-link" className="flex items-center gap-1.5 text-sage transition-colors hover:text-bronze">
              <Phone className="h-3 w-3" /> {BUSINESS.phone}
            </a>
          </div>
        </div>
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="Majestic Greens DMV" className="h-10 w-10 rounded-full" />
            <span className="font-serif text-lg font-extrabold tracking-tight text-[#0DA271]">
              Majestic <span className="text-[#12B76A]">Greens</span>
            </span>
          </Link>

          <nav data-testid="main-nav" className="hidden items-center gap-5 lg:flex">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                onClick={n.to === '/shop' ? shopReset : undefined}
                className="gold-underline text-[11px] font-semibold uppercase tracking-[0.15em] text-sage transition-colors hover:text-ink"
              >
                {n.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2 sm:gap-3">
            <a href={BUSINESS.phoneHref} data-testid="header-phone-icon" aria-label="Call the store" className="p-2 text-sage transition-colors hover:text-bronze lg:hidden">
              <Phone className="h-5 w-5" />
            </a>
            <button
              data-testid="header-search-bar"
              onClick={() => setSearchOpen(true)}
              className="hidden xl:flex w-52 items-center gap-2.5 rounded-full border border-ink/15 bg-surface px-4 py-2 text-left text-xs text-ash transition-colors hover:border-bronze/60 hover:text-sage"
            >
              <Search className="h-3.5 w-3.5 shrink-0 text-bronze" />
              <span className="truncate">Search strains, brands…</span>
            </button>
            <button
              data-testid="header-search-button"
              onClick={() => setSearchOpen(true)}
              aria-label="Search products"
              className="p-2 text-sage transition-colors hover:text-bronze xl:hidden"
            >
              <Search className="h-5 w-5" />
            </button>
            <button
              data-testid="header-cart-button"
              onClick={() => navigate('/checkout')}
              aria-label="Open cart"
              className="relative p-2 text-sage transition-colors hover:text-bronze"
            >
              <ShoppingBag className="h-5 w-5" />
            </button>
            <button
              data-testid="header-shop-now-button"
              onClick={() => navigate('/shop')}
              className="hidden bg-gold px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.2em] text-ink transition-colors duration-300 hover:bg-bronzehover sm:block"
            >
              Shop Now
            </button>
            <button
              data-testid="header-menu-button"
              onClick={() => setMenuOpen(true)}
              aria-label="Open menu"
              className="p-2 text-sage lg:hidden"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>
        <div className="border-t border-ink/10 lg:hidden">
          <button
            data-testid="mobile-search-bar"
            onClick={() => setSearchOpen(true)}
            className="flex w-full items-center gap-2.5 px-6 py-2.5 text-left text-xs text-ash"
          >
            <Search className="h-4 w-4 shrink-0 text-bronze" />
            <span className="truncate">Search strains, brands, categories…</span>
          </button>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-bone/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex h-16 items-center justify-between px-4">
              <button data-testid="mobile-menu-logo-home" onClick={() => { setMenuOpen(false); navigate('/'); }} aria-label="Go to homepage" className="p-0">
                <img src={LOGO_URL} alt="Majestic Greens DMV" className="h-10 w-10 rounded-full" />
              </button>
              <button data-testid="mobile-menu-close-button" onClick={() => setMenuOpen(false)} aria-label="Close menu" className="p-2 text-sage">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav className="flex flex-col gap-1 px-6 pt-8">
              {[{ to: '/', label: 'Home' }, ...NAV].map((n, i) => (
                <motion.div key={n.to} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.05 * i }}>
                  <NavLink
                    to={n.to}
                    data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, '-')}`}
                    onClick={(e) => { setMenuOpen(false); if (n.to === '/shop') shopReset(e); }}
                    className="block border-b border-ink/5 py-4 font-serif text-3xl text-ink transition-colors hover:text-bronze"
                  >
                    {n.label}
                  </NavLink>
                </motion.div>
              ))}
              <a href={BUSINESS.phoneHref} data-testid="mobile-nav-phone" className="mt-6 flex items-center gap-3 text-bronze">
                <Phone className="h-5 w-5" /> {BUSINESS.phone}
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />

      <div data-testid="mobile-sticky-bar" className="fixed bottom-0 z-40 flex w-full border-t border-ink/10 bg-bone/90 backdrop-blur-xl lg:hidden">
        <button data-testid="sticky-search-button" onClick={() => setSearchOpen(true)} className="flex flex-1 flex-col items-center gap-1 py-3 text-[10px] font-semibold uppercase tracking-widest text-sage">
          <Search className="h-5 w-5" /> Search
        </button>
        <button data-testid="sticky-shop-button" onClick={() => { if (window.location.pathname.startsWith('/shop') && window.location.hash.startsWith('#!')) { window.location.assign('/shop'); } else { navigate('/shop'); } }} className="flex flex-1 flex-col items-center gap-1 bg-gold/10 py-3 text-[10px] font-bold uppercase tracking-widest text-bronze">
          <ShoppingBag className="h-5 w-5" /> Shop
        </button>
        <button data-testid="sticky-cart-button" onClick={() => navigate('/checkout')} className="relative flex flex-1 flex-col items-center gap-1 py-3 text-[10px] font-semibold uppercase tracking-widest text-sage">
          <ShoppingBag className="h-5 w-5" /> Cart
        </button>
      </div>
    </>
  );
}
